import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import { BoxContent } from "../../../components/BoxView/BoxContent";
import { BoxFooter } from "../../../components/BoxView/BoxFooter";
import { Style } from "../../../../core/models/entities/Style";
import React from "react";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../../components/Typographies/BoxHeaderTypography";
import { WhiteLabelIcon } from "../../logo-editor/componenets/WhiteLabelIcon";
import { faShirt } from "@fortawesome/free-solid-svg-icons";
import TableSearchBar from "../../../components/GenericTable/SubComponents/TableHeader/TableSearchBar";
import { ManufacturerAutoComplete } from "../../../components/autocompletes/manufacturer-auto.complete";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { InputLabel } from "../../../components/Input/InputLabel";
import { InvertedPanel } from "../../../components/BoxView/InvertedPanel";
import { imageFallback } from "../../../shared/const/imageFallback";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import { CustomInput } from "../../../components/Input/CustomInput";
import { Organization } from "../../../../core/models/entities/Organization";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import LoadingElement from "../../../components/loading-element/loading-element";
import { InfinityListComponent } from "../../../shared/components/infinity-list.component";
import { StyleViewer } from "./style-viewer.component";
import { StyleCard } from "./style-card.component";
import { CustomAutoComplete } from "../../../components/Input/CustomAutoComplete";
import { VendorSearchAutoComplete } from "../../../components/Input/VendorSearchAutoComplete";
import { Role } from "../../../../core/models/entities/Role";
import { Dialog_VM } from "../../../components/Dialogs/dialog-base";
import { CustomStyleUploadDialog } from "./custom-style-upload-dialog.component";
import { CustomStyleUploadData } from "../../../../data/repositories/StyleRepository";

export interface DesignCreateForm_VM {
  translate: (keyword: string) => string;
  styles: Style[];
  isStylesLoading: boolean;
  onLoadMoreStyles: () => void;
  manufacturers: Manufacturer[];
  onFilterByManufacturer: (manufacturer: Manufacturer | null) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedStyle?: Style;
  onSelectStyle: (style: Style) => void;
  showCustomerInfo: boolean;
  customers: Organization[];
  title: string;
  onChangeTitle: (title: string) => void;
  selectedCustomer: Organization;
  onSelectCustomer: (customer: Organization) => void;
  selectedVendor: Organization | null;
  onSelectVendor: (vendor: Organization | null) => void;
  onSubmit: () => void;
  disableSubmit: boolean;
  onCancel: () => void;
  isLoading: boolean;
  role: Role;
  customStyleDialog: Dialog_VM;
  onCustomStyleSubmit: (data: CustomStyleUploadData) => void;
  isCreatingCustomStyle: boolean;
}

export default function DesignCreateForm({
  styles,
  translate: t,
  isStylesLoading,
  onLoadMoreStyles,
  manufacturers,
  onFilterByManufacturer,
  searchQuery,
  onSearchQueryChange,
  selectedStyle,
  onSelectStyle,
  showCustomerInfo,
  customers,
  onSelectCustomer,
  selectedCustomer,
  selectedVendor,
  onSelectVendor,
  onSubmit,
  onChangeTitle,
  title,
  disableSubmit,
  onCancel,
  isLoading,
  role,
  customStyleDialog,
  onCustomStyleSubmit,
  isCreatingCustomStyle,
}: DesignCreateForm_VM): React.ReactElement {
  return (
    <JetSportBox>
      {isLoading && <LoadingElement transparent className={"rounded"} />}
      <BoxContent className={"!pr-0"}>
        <StrucRow className={"w-full flex-1"}>
          <StrucCol
            size={4}
            className={"!justify-start space-y-4 border-r pr-5 pb-5"}
          >
            <BoxHeader2 className={"flex justify-between !px-0"}>
              <BoxHeaderTypography>Pick a product</BoxHeaderTypography>
              <WhiteLabelIcon icon={faShirt} />
            </BoxHeader2>
            <div className={"space-y-8"}>
              <div>
                <InputLabel>Selected Product</InputLabel>
                {selectedStyle ? (
                  <div className={"border h-32 rounded py-2"}>
                    <StrucRow>
                      <StrucCol size={4}>
                        <img
                          className={"object-contain h-28"}
                          src={
                            selectedStyle.thumbnailUrlLowResolution ||
                            selectedStyle.thumbnailUrl ||
                            selectedStyle.styleViews[0].imageUrl
                          }
                          onError={(e) => {
                            const img = e.currentTarget;
                            const fallback =
                              selectedStyle.thumbnailUrl ??
                              selectedStyle.styleViews[0].imageUrl;
                            if (fallback && img.src !== fallback)
                              img.src = fallback;
                          }}
                        />
                      </StrucCol>
                      <StrucCol className={"space-y-2"}>
                        <div>
                          <p className={"font-bold capitalize text-xs"}>
                            Model
                          </p>
                          <p>{selectedStyle.modelAndName}</p>
                        </div>
                        <div>
                          <p className={"font-bold capitalize text-xs"}>
                            Manufacturer
                          </p>
                          <p>{selectedStyle.manufacturer.name}</p>
                        </div>
                      </StrucCol>
                    </StrucRow>
                  </div>
                ) : (
                  <div
                    className={
                      "bg-gray-100 h-32 rounded p-4 flex justify-center items-center"
                    }
                  >
                    No style selected
                  </div>
                )}
              </div>
              <div className={"space-y-2"}>
                <div>
                  <InputLabel>Manufacturer</InputLabel>
                  <ManufacturerAutoComplete
                    manufacturers={manufacturers}
                    onSelected={onFilterByManufacturer}
                  />
                </div>
                <div>
                  <InputLabel>Search for Model</InputLabel>
                  <TableSearchBar
                    onSearchQueryChange={(x) =>
                      onSearchQueryChange(x.searchTerm)
                    }
                    searchQuery={searchQuery}
                  />
                </div>
                <InfinityListComponent<Style>
                  className={"!h-[530px] pt-4"}
                  currentItems={styles}
                  onLoadMore={onLoadMoreStyles}
                  isLoading={isStylesLoading}
                  renderItem={(style) => (
                    <StyleCard
                      key={style.id}
                      style={style}
                      onClick={onSelectStyle}
                      isSelected={style.id === selectedStyle?.id}
                    />
                  )}
                />
                <div className="pt-8">
                  <p className="text-xs text-gray-500 text-center mb-2">
                    Can't find your product?
                  </p>
                  <SecondaryButton
                    onClick={customStyleDialog.openDialog}
                    className="w-full"
                  >
                    Upload your own images
                  </SecondaryButton>
                </div>
              </div>
            </div>
          </StrucCol>

          {/* Custom Style Upload Dialog */}
          <CustomStyleUploadDialog
            dialogOption={customStyleDialog}
            onSubmit={onCustomStyleSubmit}
            isLoading={isCreatingCustomStyle}
          />
          <StrucCol size={8} className={"!justify-start "}>
            <BoxFooter className={"border-t justify-between"}>
              <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
              <PrimaryButton disabled={disableSubmit} onClick={onSubmit}>
                {t("continue")}
              </PrimaryButton>
            </BoxFooter>
            <InvertedPanel
              className={"rounded-tr !mx-0 !px-0 !py-5 flex justify-center"}
            >
              <BoxHeaderTypography>
                {selectedStyle?.modelAndName}
              </BoxHeaderTypography>
            </InvertedPanel>
            <div className={"bg-gray-100 border-t border-b py-2 px-2"}>
              <StrucRow>
                <StrucCol size={6} className={"px-2"}>
                  <InputWrapper label="title">
                    <CustomInput
                      className={"!bg-white !text-xs"}
                      value={title}
                      maxLength={30}
                      onChange={(e) => onChangeTitle(e.target.value)}
                      placeholder="Enter design title..."
                    />
                  </InputWrapper>
                </StrucCol>
                {role !== Role.EndUser && (
                  <StrucCol size={6} className={"px-2"}>
                    {role === Role.Admin ? (
                      <InputWrapper label="Vendor">
                        <VendorSearchAutoComplete
                          value={selectedVendor}
                          onChange={onSelectVendor}
                          placeholder="Search for vendor..."
                        />
                      </InputWrapper>
                    ) : (
                      <InputWrapper label="Customer">
                        <CustomAutoComplete<Organization>
                          options={customers}
                          getOptionKey={(org) => "" + org.id}
                          displayOption={(org) => org.name}
                          key={selectedCustomer?.id}
                          onChange={(org) => onSelectCustomer(org!)}
                          value={selectedCustomer ?? null}
                          placeholder="Search for customer..."
                        />
                      </InputWrapper>
                    )}
                  </StrucCol>
                )}
              </StrucRow>
              {showCustomerInfo && (
                <StrucRow>
                  {/* <StrucCol size={6} className={'px-2'}>
                    <InputWrapper>
                      <InputLabel>Customer Branch</InputLabel>
                      <Dropdown
                        options={[]}
                        defaultValue={false}
                        onOptionSelect={(c) => {
                          if (c) {
                            // todo: branches
                          }
                        }}
                        getOptionDisplay={(c) => 'c.name'}
                      />
                    </InputWrapper>
                  </StrucCol>*/}
                  {/*<StrucCol size={6} className={'px-2'}>
                  <InputWrapper>
                    <InputLabel>Contact</InputLabel>
                    <CustomInput className={'bg-white'} value={''}
                                 onChange={e => {
                                 }} />
                  </InputWrapper>
                </StrucCol>*/}
                </StrucRow>
              )}
            </div>
            {selectedStyle && <StyleViewer views={selectedStyle.styleViews} />}
          </StrucCol>
        </StrucRow>
      </BoxContent>
    </JetSportBox>
  );
}
